/**
 * Tiny in-process event bus for "data changed, anyone caring should
 * refresh" notifications. Most screens use `useApi` which doesn't
 * watch for foreign mutations — a vehicle created in a modal won't
 * cause the dropdown across the page to re-fetch unless we tell it.
 *
 * Pattern at call sites:
 *
 *   // After a successful create/update/delete:
 *   bus.emit('vehicle:created', { id });
 *
 *   // In any list / dropdown that reads the same data:
 *   useBusReload('vehicle:created', reload);
 *
 * No payload schema — the payload is informational. Subscribers always
 * call their own reload to be safe.
 */

import { useEffect } from "react";

type Listener = (detail?: unknown) => void;
type Topic =
  | "vehicle:created"   | "vehicle:updated"   | "vehicle:deleted"
  | "tracker:created"   | "tracker:updated"   | "tracker:deleted"
  | "driver:created"    | "driver:updated"    | "driver:deleted"
  | "geofence:created"  | "geofence:updated"  | "geofence:deleted"
  | "fuel:created"      | "fuel:deleted"
  | "document:created"  | "document:updated"  | "document:deleted"
  | "maintenance:created" | "maintenance:updated" | "maintenance:deleted"
  | "site:created"      | "site:updated"      | "site:deleted"
  | "asset:created"     | "asset:updated"     | "asset:deleted"
  | "client:created"    | "client:updated"    | "client:deleted"
  | "ticket:created"    | "ticket:updated"    | "ticket:deleted"
  | "form:created"      | "form:updated"      | "form:deleted"
  | "form-entry:created"| "form-entry:updated"| "form-entry:deleted"
  | "route:created"     | "route:updated"     | "route:deleted"
  // Auth side-channel: api() emits this on a 401 from an authed
  // request so the AuthProvider can clear user state and let
  // RequireAuth bounce to /auth/login. Without it the SPA happily
  // navigates around with no data after the cookie expires.
  | "auth:expired"
  // Customer-portal session died. Separate from auth:expired so a portal 401
  // doesn't sign the staff user out in the same tab, or vice versa (SUP-21).
  | "portal-auth:expired"
  // api() emits this on a 402 (subscription lapsed / read-only / user
  // limit) so the shell can show a renew banner instead of a raw error.
  | "billing:required"
  // Catch-all when nothing more specific exists.
  | "data:changed";

const listeners = new Map<Topic, Set<Listener>>();

export const bus = {
  emit(topic: Topic, detail?: unknown): void {
    const set = listeners.get(topic);
    if (set) set.forEach(l => { try { l(detail); } catch (e) { console.warn("bus listener", e); } });
    // Also dispatch the catch-all so a generic refresh button can
    // listen to one topic and still react to anything.
    if (topic !== "data:changed") {
      const all = listeners.get("data:changed");
      if (all) all.forEach(l => { try { l({topic, detail}); } catch (e) { console.warn("bus listener", e); } });
    }
  },
  on(topic: Topic, listener: Listener): () => void {
    let set = listeners.get(topic);
    if (!set) { set = new Set(); listeners.set(topic, set); }
    set.add(listener);
    return () => { set!.delete(listener); };
  },
};

/**
 * Subscribe a callback (typically `reload` from `useApi`) to one or
 * more topics. Listener is auto-cleaned on unmount.
 */
export function useBusReload(topics: Topic | Topic[], cb: () => void): void {
  useEffect(() => {
    const list = Array.isArray(topics) ? topics : [topics];
    const unsubs = list.map(t => bus.on(t, cb));
    return () => { unsubs.forEach(u => u()); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(topics) ? topics.join(",") : topics]);
}
